import { sum } from 'lodash'
import moment from 'moment/moment'
import { ReactNode, useMemo } from 'react'

import { Invoice } from '../../models/Invoice'
import { Payment } from '../../models/Payment'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { invoiceCategoryOptions } from '../settings/Options'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    invoice: Invoice
    isWorkflowReviewStep: boolean
    client: User
    payments?: Payment[]
}

export const InvoiceView = ({ invoice, isWorkflowReviewStep, client, payments }: Props) => {
    const receipt = useMemo((): Receipt => {
        const items = invoice.lines.map((line, lineIndex) => {
            const { title, subtitle, thirdLine, tertiaryView } = ((): {
                title: string
                subtitle: string
                thirdLine?: string
                tertiaryView?: ReactNode
            } => {
                if (line.type === 'expense') {
                    return {
                        title:
                            line.category === undefined
                                ? 'None'
                                : (invoiceCategoryOptions.find(
                                      (option) => option.value === line.category
                                  )?.label ?? ''),
                        subtitle: currencyFormat(line.total),
                        tertiaryView: (
                            <Icon icon="coins.large" className="size-12" background="round" />
                        ),
                    }
                }
                else if (line.type === 'booking') {
                    return {
                        title: moment(line.booking?.date).format('ddd, MMMM Do'),
                        subtitle: `${moment(line.booking?.date).format('h:mm A')} - ${moment(line.booking?.endDate).format('h:mm A')}`,
                        thirdLine: line.booking?.item?.name,
                        tertiaryView: (
                            <Icon icon="calendar.large" className="size-12" background="round" />
                        ),
                    }
                }
                else {
                    return {
                        title: line.item?.name ?? '',
                        subtitle: currencyFormat(line.rate ?? 0),
                        tertiaryView:
                            line.item?.iconConfig === undefined ? undefined : line.item?.iconConfig
                                  .type === 'image' ? (
                                <img
                                    src={line.item.iconConfig.uri}
                                    className="size-12 rounded-full"
                                />
                            ) : (
                                <Icon
                                    icon={line.item.iconConfig.icon}
                                    background="round"
                                    className="size-12"
                                />
                            ),
                    }
                }
            })()

            return {
                id: line.id ?? lineIndex.toString(),
                title,
                subtitle,
                thirdLine,
                tertiaryView,
                quantity: line.quantity ?? 1,
                price: line.total ?? 0,
            }
        })

        return {
            items,
            modifiers: {
                subtotal: {
                    amount:
                        invoice.lines.length > 0
                            ? sum(invoice.lines.map((line) => line.total ?? 0))
                            : (invoice.total ?? 0),
                },
                giftCertificate:
                    invoice.gift !== undefined
                        ? {
                              selectedGift: invoice.gift,
                          }
                        : undefined,
                discount: invoice.discount !== undefined && invoice.discount !== 0 ? {} : undefined,
                gratuity:
                    ((invoice.allowAnyPayment ?? '') === '' || !!invoice.markPaidDate) &&
                    invoice.gratuityAmount > 0
                        ? {
                              amount: invoice.gratuityAmount,
                              percent:
                                  invoice.gratuityType === 'percent' ? invoice.gratuity : undefined,
                          }
                        : undefined,
                salesTax:
                    invoice.taxcode !== undefined
                        ? {
                              amount: invoice.taxAmount ?? null,
                              parenthetical: `${invoice.taxcode.rate ?? 0}%`,
                          }
                        : undefined,
                surcharge: invoice.allowSurcharge
                    ? {
                          amount: invoice.surchargeAmount,
                      }
                    : undefined,
            },
            total: invoice.chargeAmount,
        }
    }, [invoice])

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-2">
                        <Heading size="lg">{currencyFormat(invoice.chargeAmount, true)}</Heading>
                        <Label size="lg" className="font-normal text-gray-600">
                            {invoice.due_date?.isAfter(invoice.date) ? `Due ${invoice.due_date?.format('MMM Do, YYYY')}` : invoice.date?.format('MMM Do, YYYY')}
                        </Label>
                        <Label size="lg" className="font-normal text-gray-600">
                            Invoice #
                            {invoice.number !== undefined ? invoice.number : ' (to be generated)'}
                        </Label>

                        <Label size="lg" className="font-normal text-gray-600">
                            {invoice.statusString}
                        </Label>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {invoice.type === 'schedule' && !!invoice.nextInvoiceDate && (
                            <BaseRecordView.IconField
                                icon="calendar.large"
                                title={invoice.nextInvoiceDate.format('MMM D, YYYY')}
                                subtitle="Next invoice date"
                            />
                        )}

                        {!!invoice.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={invoice.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={invoice.subaccount.name ?? ''}
                                subtitle={invoice.subaccount.labelString}
                                href={`/subaccounts/${invoice.subaccount.id}`}
                            />
                        )}

                        <BaseRecordView.IconField
                            icon={
                                <ContactAvatar
                                    user={client}
                                    hasHoverEffect={!isWorkflowReviewStep}
                                />
                            }
                            title={client.name ?? ''}
                            subtitle="Client"
                            secondaryView={
                                !isWorkflowReviewStep ? (
                                    <a href={`/messages/${client.id}`}>
                                        <Button intent="secondary" size="small">
                                            Chat
                                        </Button>
                                    </a>
                                ) : undefined
                            }
                            href={!isWorkflowReviewStep ? `/clients/${client.id}` : undefined}
                        />
                    </div>
                </Card>

                {!isWorkflowReviewStep && (
                    <Card size="sm" title="Status">
                        <RecordStatusChecklist
                            items={invoice.statuses.map((status) => ({
                                title: status.title,
                                subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                                status: status.date !== undefined ? 'checked' : 'empty',
                            }))}
                            className="mt-6"
                        />
                    </Card>
                )}
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <Card>
                    <Heading size="sm" className="mb-4 text-gray-700">
                        Invoice details
                    </Heading>

                    <WorkflowPage.ReceiptView
                        receipt={receipt}
                        transaction={invoice}
                        modifierGapSize="small"
                    />
                </Card>

                {payments !== undefined && payments.length > 0 && (
                    <BaseRecordView.PaymentsSection payments={payments} />
                )}

                {(invoice.memo ?? '').trim().length > 0 && (
                    <Card>
                        <Heading size="sm" className="text-gray-700">
                            Memo
                        </Heading>

                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {invoice.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
